import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { KeyboardAccessoryNative } from '@/lib/native/keyboard-accessory'
import { isKeyboardVisible, subscribeKeyboard } from '@/lib/keyboard'

// Keyboard-bearing text fields only. Buttons, Radix Select triggers, switches,
// checkboxes and the hidden file inputs behind photo pickers never take the
// text keyboard; date/time inputs swap it for a picker wheel, so navigating
// into them would tear down the accessory session mid-flight.
const FIELD_SELECTOR = [
  'input' +
    [
      'hidden',
      'file',
      'checkbox',
      'radio',
      'button',
      'submit',
      'reset',
      'range',
      'color',
      'date',
      'time',
      'datetime-local',
      'month',
      'week',
    ]
      .map((t) => `:not([type="${t}"])`)
      .join(''),
  'textarea',
].join(', ')

type FieldEl = HTMLInputElement | HTMLTextAreaElement

/* ------------------------------------------------------------------ */
/*  Singleton manager                                                  */
/*                                                                     */
/*  The native bar (KeyboardAccessoryPlugin) is process-global, so one */
/*  module-level manager owns its lifecycle: enabled while at least    */
/*  one container is registered (refcounted — a picker sub-drawer      */
/*  closing must not kill the bar for the drawer underneath), with a   */
/*  single set of listeners routing events to whichever container      */
/*  holds the focused field. Nothing is cached: fields and the current */
/*  index are re-derived from the DOM at event time so conditional     */
/*  fields appearing/disappearing can never leave stale arrow state.   */
/* ------------------------------------------------------------------ */

const containers = new Set<HTMLElement>()
const subscribers = new Set<() => void>()
let detach: (() => void) | null = null

// offsetParent is unreliable inside position:fixed drawers; prefer
// checkVisibility (Safari 17.4+) and fall back to rendered boxes.
const isVisible = (el: HTMLElement) =>
  el.checkVisibility ? el.checkVisibility() : el.getClientRects().length > 0

const fieldsIn = (container: HTMLElement): FieldEl[] =>
  Array.from(container.querySelectorAll<FieldEl>(FIELD_SELECTOR)).filter(
    (el) => !el.disabled && !el.readOnly && isVisible(el)
  )

// Drawers portal to document.body as siblings, so at most one registered
// container contains the focused element.
function activeContainer(): HTMLElement | null {
  const active = document.activeElement
  if (!active) return null
  for (const c of containers) if (c.contains(active)) return c
  return null
}

function syncState() {
  const container = activeContainer()
  const fields = container ? fieldsIn(container) : []
  const i = fields.indexOf(document.activeElement as FieldEl)
  KeyboardAccessoryNative.setState({
    hasPrev: i > 0,
    hasNext: i >= 0 && i < fields.length - 1,
  }).catch(() => {})
}

function navigate(dir: 1 | -1) {
  const container = activeContainer()
  if (!container) return
  const fields = fieldsIn(container)
  const target = fields[fields.indexOf(document.activeElement as FieldEl) + dir]
  if (!target) return
  // The keyboard is already up (we only navigate from a focused field), so
  // moving focus synchronously between inputs keeps it visible.
  target.focus()
  target.scrollIntoView({ block: 'center', behavior: 'smooth' })
  syncState()
}

const notifySubscribers = () => subscribers.forEach((fn) => fn())

function attach() {
  let cancelled = false
  const handles: Array<{ remove: () => void }> = []
  const track = (p: Promise<{ remove: () => void }>) =>
    p
      .then((h) => {
        if (cancelled) h.remove()
        else handles.push(h)
      })
      .catch(() => {})

  KeyboardAccessoryNative.enable().catch(() => {})
  track(KeyboardAccessoryNative.addListener('prev', () => navigate(-1)))
  track(KeyboardAccessoryNative.addListener('next', () => navigate(1)))
  track(
    KeyboardAccessoryNative.addListener('done', () => {
      ;(document.activeElement as HTMLElement | null)?.blur()
    })
  )
  // Keyboard show/hide comes from the global store (src/lib/keyboard.ts).
  const unsubscribeKeyboard = subscribeKeyboard(() => {
    if (isKeyboardVisible()) syncState()
    notifySubscribers()
  })

  const onFocusIn = () => {
    syncState()
    notifySubscribers()
  }
  // Conditional fields can appear/disappear while typing; refresh the arrows.
  let rafId = 0
  const onInput = () => {
    if (rafId) return
    rafId = requestAnimationFrame(() => {
      rafId = 0
      syncState()
    })
  }
  document.addEventListener('focusin', onFocusIn)
  document.addEventListener('input', onInput, true)

  detach = () => {
    cancelled = true
    handles.forEach((h) => h.remove())
    unsubscribeKeyboard()
    document.removeEventListener('focusin', onFocusIn)
    document.removeEventListener('input', onInput, true)
    if (rafId) cancelAnimationFrame(rafId)
    KeyboardAccessoryNative.disable().catch(() => {})
    detach = null
  }
}

function register(el: HTMLElement): () => void {
  containers.add(el)
  if (containers.size === 1) attach()
  return () => {
    containers.delete(el)
    if (containers.size === 0) detach?.()
  }
}

/**
 * Drives the native iOS keyboard accessory bar (Prev/Next/Done) across the
 * text fields inside the returned `containerRef` container, discovered in
 * DOM order — no per-field markup needed. Mounted once in `DrawerContent`,
 * so every drawer gets the bar automatically; standalone screens (e.g. the
 * task checklist) opt in with the same hook.
 *
 * `containerRef` is a callback ref so the container registers whenever it
 * actually attaches — screens that render a loading skeleton first (no
 * container in the tree yet) still get the bar once real content mounts.
 *
 * `navActive` is true while the keyboard is up with focus inside this
 * container, so screens can hide their bottom action bars while typing.
 * No-op off iOS.
 */
export function useFieldNavigator() {
  const [el, setEl] = useState<HTMLElement | null>(null)
  const [navActive, setNavActive] = useState(false)

  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios') return
    if (!el) return
    const recompute = () =>
      setNavActive(isKeyboardVisible() && el.contains(document.activeElement))
    subscribers.add(recompute)
    const unregister = register(el)
    return () => {
      subscribers.delete(recompute)
      unregister()
      setNavActive(false)
    }
  }, [el])

  return { containerRef: setEl, navActive }
}
